import { $ppidAtom } from '@client/core/atoms/config.js';
import * as RelevantDigital from '@client/core/relevantDigital/index.js';
import { debugLog, noop } from '@schibsted-nmp/advertising-shared';

import type { Api } from '../Api/index.js';
import type { Pbjs } from '../Pbjs.js';
import { ConsentManagement } from './ConsentManagement.js';
import type { PrebidRequester } from './PrebidRequester.js';

export namespace PrebidConfig {
  export function apply(
    relevantDigital: Api.Loaded,
    customConsentVendorsMap: ConsentManagement.CustomVendors.Map,
    userSyncSettings: PrebidRequester.SiteData['userSync']
  ) {
    relevantDigital.addPrebidConfig({
      enableTIDs: true,

      consentManagement: {
        gdpr: {
          cmpApi: 'iab',
          timeout: 1000,
          defaultGdprScope: true,
          rules: ConsentManagement.Rules.from(customConsentVendorsMap)
        }
      },

      userSync: {
        iframeEnabled: true,

        filterSettings: {
          iframe: {
            bidders: '*',
            filter: 'include'
          }
        }
      }
    });

    $ppidAtom.subscribe(UserSync.onPpidChange(userSyncSettings));
  }

  export namespace UserSync {
    export function onPpidChange(
      userSyncSettings: PrebidRequester.SiteData['userSync']
    ) {
      return (ppid: $ppidAtom.Value) => {
        configure(ppid, userSyncSettings).catch(noop);
      };
    }

    export async function configure(
      ppid: $ppidAtom.Value,
      userSyncSettings: PrebidRequester.SiteData['userSync']
    ) {
      try {
        const userIds: NonNullable<Pbjs.Common.Config.UserSync['userIds']> = [
          ...Adform.getUserIds(ppid.adform, userSyncSettings?.adform)
        ];

        if (!userIds.length) {
          debugLog('No user IDs available, skipping user sync via Prebid');
          return;
        }

        for (const { name, bidders, params } of userIds) {
          debugLog(
            `Setting up ${name} sync via Prebid for ${bidders.join(', ')}`,
            params
          );
        }

        await RelevantDigital.Api().addPrebidConfig({ userSync: { userIds } });
      } catch (error: unknown) {
        debugLog('Failed configuring user sync via Prebid', error);
      }
    }

    export namespace Eid {
      export function create(
        source: string,
        id: string
      ): Pbjs.Common.Config.UserSync.UserId.Params.Eid {
        return {
          source,
          uids: [
            {
              id,
              atype: 1,
              ext: { stype: 'ppuid' }
            }
          ]
        };
      }

      export function* from(
        ...keyValuePairs: ReadonlyArray<
          readonly [source: string | undefined, id: string | undefined]
        >
      ) {
        for (const [source, id] of keyValuePairs) {
          if (!source || !id) continue;
          yield create(source, id);
        }
      }
    }

    export namespace Adform {
      export function* getUserIds(
        ppidObject: $ppidAtom.Value['adform'],
        userSyncSettings: PrebidRequester.SiteData.UserSync['adform']
      ): Generator<Pbjs.Common.Config.UserSync.UserId, void, void> {
        if (!userSyncSettings) {
          debugLog(
            'No user sync settings provided for Adform, skipping PPID sync for Adform via Prebid'
          );
          return;
        }

        const { sourceNames, alias } = userSyncSettings;

        const eids = Array.from(
          Eid.from(
            [sourceNames?.loggedInId, ppidObject?.ppId1],
            [sourceNames?.deviceId, ppidObject?.ppId2]
          )
        );

        if (!eids.length) {
          debugLog(
            'No PPIDs provided, skipping PPID sync for Adform via Prebid'
          );
          return;
        }

        yield {
          name: 'pubProvidedId',
          bidders: [alias],
          params: { eids }
        };
      }
    }
  }
}
